import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Mail, Lock, ArrowLeft, ArrowRight, Loader2, ShieldCheck, KeyRound, Eye, EyeOff } from "lucide-react";
import api from "../../lib/api";

export const AUTH_ERR = {
  invalid_code: ["Código incorrecto. Revísalo e inténtalo otra vez.", "Wrong code. Check it and try again."],
  code_expired: ["El código ha caducado. Pide uno nuevo.", "The code expired. Request a new one."],
  too_many_attempts: ["Demasiados intentos. Espera un poco y vuelve a probar.", "Too many attempts. Wait a bit and try again."],
  wait_resend: ["Espera unos segundos antes de pedir otro código.", "Wait a few seconds before requesting another code."],
  email_send_failed: ["No pudimos enviar el correo. Inténtalo en un momento.", "We couldn't send the email. Try again shortly."],
  email_unavailable: ["La recuperación por correo no está disponible ahora mismo.", "Email recovery is unavailable right now."],
  weak_password: ["La contraseña debe tener al menos 6 caracteres.", "Password must be at least 6 characters."],
  invalid_email: ["Introduce un email válido.", "Enter a valid email."],
  email_exists: ["Ya existe una cuenta con ese email. Inicia sesión.", "An account with that email already exists. Sign in."],
};
export const errText = (ex, L) => {
  const d = ex?.response?.data?.detail;
  const m = typeof d === "string" && AUTH_ERR[d];
  return m ? L(m[0], m[1]) : L("Algo salió mal. Inténtalo de nuevo.", "Something went wrong. Try again.");
};

const Head = ({ icon: I, title, sub, onBack, L }) => (
  <>
    <button type="button" onClick={onBack} data-testid="auth-step-back" className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white transition-colors"><ArrowLeft className="h-4 w-4" />{L("Volver", "Back")}</button>
    <span className="mt-6 grid h-14 w-14 place-items-center rounded-2xl bg-yellow-400/10 border border-yellow-400/30"><I className="h-7 w-7 text-yellow-400" /></span>
    <h2 className="mt-5 text-3xl sm:text-4xl font-black tracking-tight" data-testid="auth-step-title">{title}</h2>
    <p className="mt-2 text-sm text-zinc-400 leading-relaxed">{sub}</p>
  </>
);

const OtpInput = ({ value, onChange }) => {
  const refs = useRef([]);
  useEffect(() => { refs.current[0]?.focus(); }, []);
  const set = (i, v) => {
    const d = v.replace(/\D/g, "");
    if (d.length > 1) { onChange(d.slice(0, 6)); refs.current[Math.min(5, d.length)]?.focus(); return; }
    const arr = value.padEnd(6, " ").split(""); arr[i] = d || " ";
    onChange(arr.join("").trimEnd());
    if (d && i < 5) refs.current[i + 1]?.focus();
  };
  return (
    <div className="mt-7 flex justify-between gap-2" data-testid="otp-input">
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <input key={i} ref={(el) => (refs.current[i] = el)} inputMode="numeric" autoComplete={i === 0 ? "one-time-code" : "off"} maxLength={6}
          value={(value[i] || "").trim()} onChange={(e) => set(i, e.target.value)} data-testid={`otp-digit-${i}`}
          onKeyDown={(e) => e.key === "Backspace" && !(value[i] || "").trim() && i > 0 && refs.current[i - 1]?.focus()}
          className="h-14 w-full max-w-[56px] rounded-xl border border-white/15 bg-white/[0.04] text-center text-2xl font-black text-yellow-300 focus:border-yellow-400 focus:bg-white/[0.07] focus:outline-none transition-colors" />
      ))}
    </div>
  );
};

const Submit = ({ busy, children, testid }) => (
  <button type="submit" disabled={busy} data-testid={testid}
    className="group mt-6 w-full flex items-center justify-center gap-2 rounded-xl bg-yellow-400 py-3.5 text-sm font-extrabold text-black hover:bg-yellow-300 disabled:opacity-60 shadow-[0_0_30px_-8px_rgba(250,204,21,.7)] transition-colors">
    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>{children}<ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" /></>}
  </button>
);

const Err = ({ err }) => err ? <p className="mt-4 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300" data-testid="auth-step-error">{err}</p> : null;

const Resend = ({ ch, setCh, L }) => {
  const [left, setLeft] = useState(45);
  const [msg, setMsg] = useState("");
  useEffect(() => { const t = setInterval(() => setLeft((s) => Math.max(0, s - 1)), 1000); return () => clearInterval(t); }, [ch.challenge_id]);
  const again = async () => {
    try { const { data } = await api.post("/auth/resend-code", { challenge_id: ch.challenge_id }); setCh(data); setLeft(45); setMsg(L("Código reenviado ✓", "Code resent ✓")); }
    catch (ex) { setMsg(errText(ex, L)); }
  };
  return (
    <p className="mt-5 text-center text-sm text-zinc-400">{L("¿No te ha llegado? Mira en spam o", "Didn't get it? Check spam or")}{" "}
      <button type="button" disabled={left > 0} onClick={again} data-testid="otp-resend" className="font-bold text-yellow-400 hover:underline disabled:text-zinc-500 disabled:no-underline">
        {left > 0 ? L(`reenvía en ${left}s`, `resend in ${left}s`) : L("reenviar código", "resend code")}</button>
      {msg && <span className="block mt-2 text-xs text-zinc-300" data-testid="otp-resend-msg">{msg}</span>}
    </p>
  );
};

// Step after email+password: type the 6-digit code that was emailed.
export const VerifyStep = ({ ch, setCh, onBack, onVerify, L }) => {
  const [code, setCode] = useState(""); const [busy, setBusy] = useState(false); const [err, setErr] = useState("");
  const submit = async (e) => {
    e?.preventDefault(); if (code.length < 6) return; setBusy(true); setErr("");
    try { await onVerify(ch.challenge_id, code); } catch (ex) { setErr(errText(ex, L)); setCode(""); } finally { setBusy(false); }
  };
  useEffect(() => { if (code.length === 6) submit(); }, [code]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <motion.form key="verify" onSubmit={submit} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} data-testid="verify-step">
      <Head icon={ShieldCheck} L={L} onBack={onBack} title={L("Revisa tu correo", "Check your email")}
        sub={<>{L("Te hemos enviado un código de 6 dígitos a", "We sent a 6-digit code to")} <b className="text-white" data-testid="verify-email">{ch.email}</b>.</>} />
      <OtpInput value={code} onChange={setCode} />
      <Err err={err} />
      <Submit busy={busy} testid="verify-submit">{L("Verificar y entrar", "Verify and continue")}</Submit>
      <Resend ch={ch} setCh={setCh} L={L} />
    </motion.form>
  );
};

const Input = ({ icon: I, right, ...p }) => (
  <label className="group relative mt-3 flex items-center rounded-xl border border-white/10 bg-white/[0.03] focus-within:border-yellow-400/70 transition-colors">
    <I className="absolute left-3.5 h-4 w-4 text-zinc-500 group-focus-within:text-yellow-400" />
    <input {...p} className="w-full bg-transparent py-3.5 pl-10 pr-11 text-sm text-white placeholder:text-zinc-500 focus:outline-none" />{right}
  </label>
);

// Forgot password: email -> code + new password, all inside the same card.
export const ForgotStep = ({ initialEmail, onBack, onReset, L }) => {
  const [email, setEmail] = useState(initialEmail || ""); const [ch, setCh] = useState(null);
  const [code, setCode] = useState(""); const [pw, setPw] = useState(""); const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false); const [err, setErr] = useState("");
  const ask = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try { const { data } = await api.post("/auth/forgot-password", { email }); setCh(data); } catch (ex) { setErr(errText(ex, L)); } finally { setBusy(false); }
  };
  const reset = async (e) => {
    e.preventDefault(); if (code.length < 6) { setErr(L("Escribe los 6 dígitos del código.", "Enter the 6-digit code.")); return; }
    setBusy(true); setErr("");
    try { await onReset(ch.challenge_id, code, pw); } catch (ex) { setErr(errText(ex, L)); } finally { setBusy(false); }
  };
  if (!ch) return (
    <motion.form key="forgot" onSubmit={ask} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} data-testid="forgot-step">
      <Head icon={KeyRound} L={L} onBack={onBack} title={L("¿Olvidaste tu contraseña?", "Forgot your password?")}
        sub={L("No pasa nada. Escribe tu correo y te enviaremos un código para crear una contraseña nueva.", "No worries. Enter your email and we'll send you a code to create a new password.")} />
      <div className="mt-4"><Input icon={Mail} type="email" required placeholder="email@ejemplo.com" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email" autoComplete="email" /></div>
      <Err err={err} />
      <Submit busy={busy} testid="forgot-submit">{L("Enviar código", "Send code")}</Submit>
    </motion.form>
  );
  return (
    <motion.form key="reset" onSubmit={reset} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} data-testid="reset-step">
      <Head icon={ShieldCheck} L={L} onBack={() => setCh(null)} title={L("Crea tu nueva contraseña", "Create your new password")}
        sub={<>{L("Si existe una cuenta con", "If an account exists for")} <b className="text-white">{ch.email}</b>{L(", te hemos enviado un código de 6 dígitos.", ", we sent a 6-digit code.")}</>} />
      <OtpInput value={code} onChange={setCode} />
      <Input icon={Lock} type={show ? "text" : "password"} required minLength={6} placeholder={L("Nueva contraseña", "New password")} value={pw} onChange={(e) => setPw(e.target.value)} data-testid="reset-password" autoComplete="new-password"
        right={<button type="button" onClick={() => setShow((s) => !s)} className="absolute right-3 text-zinc-500 hover:text-white">{show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>} />
      <Err err={err} />
      <Submit busy={busy} testid="reset-submit">{L("Cambiar contraseña y entrar", "Change password and sign in")}</Submit>
      <Resend ch={ch} setCh={setCh} L={L} />
    </motion.form>
  );
};
